/**
 * Sesión del usuario y negocio activo, compartidos por toda la web.
 */
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ErrorApi, fijarNegocioActivo, negocioActivo } from './api';
import type { MembresiaNegocio, SeguridadSesion, Usuario } from './tipos';

interface DatosSesion {
  cargando: boolean;
  configurado: boolean;
  requiereCodigo: boolean;
  usuario: Usuario | null;
  seguridad: SeguridadSesion | null;
  negocios: MembresiaNegocio[];
  negocio: MembresiaNegocio | null;
  cambiarNegocio: (id: string) => void;
  recargar: () => Promise<void>;
  salir: () => Promise<void>;
}

const Contexto = createContext<DatosSesion | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const estado = useQuery({ queryKey: ['auth', 'estado'], queryFn: () => api.get<{ configurado: boolean; requiereCodigo: boolean }>('/auth/estado') });
  const yo = useQuery({
    queryKey: ['auth', 'yo'],
    queryFn: async () => {
      try {
        return await api.get<{ usuario: Usuario; negocios: MembresiaNegocio[]; seguridad: SeguridadSesion }>('/auth/yo');
      } catch (e) {
        if (e instanceof ErrorApi && e.estado === 401) return null;
        throw e;
      }
    },
    enabled: estado.data?.configurado === true,
    retry: false,
  });

  // El negocio activo es estado de React (para que la cabecera y los avisos se actualicen al cambiarlo)
  // y además se guarda en `api.ts`, de donde las peticiones toman la cabecera X-Negocio-Id.
  const [idActivo, setIdActivo] = useState<string | null>(() => negocioActivo());
  const negocios = yo.data?.negocios ?? [];
  const negocio = negocios.find((n) => n.negocioId === idActivo) ?? negocios[0] ?? null;

  // Se fija antes de pintar las páginas: sus consultas necesitan ya la cabecera del negocio.
  if (negocio && negocio.negocioId !== negocioActivo()) fijarNegocioActivo(negocio.negocioId);

  const valor: DatosSesion = {
    cargando: estado.isLoading || (estado.data?.configurado === true && yo.isLoading),
    configurado: estado.data?.configurado ?? false,
    requiereCodigo: estado.data?.requiereCodigo ?? false,
    usuario: yo.data?.usuario ?? null,
    seguridad: yo.data?.seguridad ?? null,
    negocios,
    negocio,
    cambiarNegocio: (id) => {
      fijarNegocioActivo(id);
      setIdActivo(id);
      // Los datos de las pantallas son de otro negocio: se descartan y las visibles se vuelven a pedir.
      void qc.resetQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
    },
    recargar: async () => {
      await qc.invalidateQueries({ queryKey: ['auth'] });
    },
    salir: async () => {
      await api.post('/auth/salida').catch(() => undefined);
      fijarNegocioActivo(null);
      setIdActivo(null);
      // Sin sesión: se vacía el usuario ya (la pantalla pasa al acceso) y se descartan los datos en memoria.
      qc.setQueryData(['auth', 'yo'], null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
      await qc.invalidateQueries({ queryKey: ['auth', 'estado'] });
    },
  };
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): DatosSesion {
  const c = useContext(Contexto);
  if (!c) throw new Error('useSesion fuera de ProveedorSesion');
  return c;
}
